import { formatDistance } from "date-fns";
import store from "../redux/store";
import config from "../config";
import dayjs from "dayjs";
import { zonedTimeToUtc, formatInTimeZone } from "date-fns-tz";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const dateTimeFormats = config.dateTimeFormats;
const getTimezone = () => store.getState().auth.timezone;
const getDTFormat = () =>
  (
    dateTimeFormats?.find((dtf) => dtf?.timezone === getTimezone()) ||
    dateTimeFormats?.find((dtf) => dtf?.timezone === "")
  ).format;

export default function format_date(date) {
  if (date) {
    const utcDate = `${date.replace(" ", "T")}Z`;
    return `${formatInTimeZone(new Date(utcDate), getTimezone(), getDTFormat())}
        ${formatDistance(new Date(utcDate), new Date(), {
          addSuffix: true,
          includeSeconds: true
        })} `;
  }

  return date;
}

export function clearTnZ(value) {
  return value.replace("T", " ").slice(0, -5);
}

export function getDateTimeWith12(dateTime) {
  const utcDateTime = new Date(`${dateTime.replace(" ", "T")}Z`);

  return `${formatInTimeZone(
    utcDateTime,
    getTimezone(),
    getDTFormat()
  )} ${formatDistance(utcDateTime, new Date(), {
    addSuffix: true,
    includeSeconds: true
  })}`;
}

export function getDateTimeWith12R(dateTime) {
  // return dateTime ? format(new Date(`${dateTime.replace(" ", "T")}Z`), "hh:mm a, d MMM yyyy") : "";
  return dateTime
    ? formatInTimeZone(
        new Date(`${dateTime.replace(" ", "T")}Z`),
        getTimezone(),
        getDTFormat()
      )
    : "";
}

export function getDateTimeText(dateTime) {
  // return format(new Date(`${dateTime.replace(" ", "T")}Z`), "do MMM, p");
  return formatInTimeZone(
    new Date(`${dateTime.replace(" ", "T")}Z`),
    getTimezone(),
    getDTFormat()
  );
}

export function getUnixTimeStampFor(dateTime) {
  return new Date(dateTime).getTime() / 1000;
}

export function getCurrentUnixTimestamp() {
  const timeWithSecs = new Date().getTime() / 1000;
  return timeWithSecs - (timeWithSecs % 100);
}

export function getCurrentDate() {
  return formatInTimeZone(new Date(), getTimezone(), "yyyy-MM-dd");
}

export function getCurrentDatetime() {
  return formatInTimeZone(new Date(), getTimezone(), "yyyy-MM-dd HH:mm");
}

export function getFormatedDatetime(dateTime) {
  return formatInTimeZone(
    `${dateTime.replace(" ", "T")}Z`,
    getTimezone(),
    "yyyy-MM-dd HH:mm"
  );
}

export function getDateBefore(days) {
  let newDate = new Date();
  newDate.setDate(newDate.getDate() - days);

  return formatInTimeZone(newDate, getTimezone(), "yyyy-MM-dd");
}

export function getUTCDatetimeStart(dateTime) {
  return clearTnZ(
    zonedTimeToUtc(dateTime + " 00:00:00", getTimezone()).toISOString()
  );
}

export function getUTCDatetimeEnd(dateTime) {
  return clearTnZ(
    zonedTimeToUtc(dateTime + " 23:59:59", getTimezone()).toISOString()
  );
}

export function getUTCDatetime(dateTime) {
  return clearTnZ(zonedTimeToUtc(dateTime, getTimezone()).toISOString());
}

export function getTimePeriodsFor(code) {
  const currentDate = getCurrentDate();

  switch (code) {
    case 1:
      return {
        from: currentDate,
        to: currentDate
      };
    case 2:
      return {
        from: getDateBefore(1),
        to: getDateBefore(1)
      };
    case 3:
      return {
        from: getDateBefore(6),
        to: currentDate
      };
    case 4:
      return {
        from: getDateBefore(29),
        to: currentDate
      };
    default:
      return {
        from: "",
        to: ""
      };
  }
}

export const formatDate = (date) => {
  return date ? dayjs(date).format("YYYY-MM-DD HH:mm:ss") : "";
};

// Function to parse the time frame
export const parseTimeFrame = (timeFrame) => {
  const valueMatch = timeFrame.match(/\d+/); // Extracts the numeric value
  const unitMatch = timeFrame.match(/[a-zA-Z]+/); // Extracts the time unit

  // Default to empty strings if there's no match
  const value = valueMatch ? valueMatch[0] : "";
  const unit = unitMatch ? unitMatch[0] : "days"; // Default to 'days' if no unit is found

  return { value, unit };
};

export const extractTimezone = (timezone) => {
  return timezone?.split(")")[1]?.trim() || null; // Split by ')' and get the second part
};

export const formatTimeZone = (timezone) => {
  const gmtOffset = timezone?.label.match(/\(GMT[^\)]*\)/)[0]; // Extracts (GMT -10:00)
  const region = timezone?.value; // Extracts the region name like Pacific/Honolulu
  return `${gmtOffset} ${region}`; // Combines them in the format (GMT -10:00) Pacific/Honolulu
};

export function convertUtcToTimezone(utcDate, timeZone) {
  if (!utcDate || !timeZone) return "";

  // Convert to ISO format and create Date object
  const utcDateObj = new Date(utcDate.replace(" ", "T") + "Z");
  if (isNaN(utcDateObj.getTime())) return "";

  // Format: "2 January 2025 3:30:00 PM"
  const options = {
    timeZone,
    year: "numeric",
    month: "long", // full month name
    day: "numeric",
    hour: "numeric", // no leading zero in 12-hour format
    minute: "2-digit",
    second: "2-digit",
    hour12: true // enable AM/PM
  };

  const formatter = new Intl.DateTimeFormat("en-GB", options);
  const parts = formatter.formatToParts(utcDateObj);

  const get = (type) => parts.find((p) => p.type === type)?.value || "";

  // Arrange: day month year time AM/PM
  return `${get("day")} ${get("month")} ${get("year")} ${get("hour")}:${get(
    "minute"
  )}:${get("second")} ${get("dayPeriod")}`;
}

export const getTimeZoneWithGMT = (timezone) => {
  if (!timezone?.value) return "";

  try {
    const tz = timezone.value;

    // Get GMT offset string like "GMT+3" or "GMT-5"
    const gmtOffset = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset"
    })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value; // "GMT+3"

    // Remove "GMT" and keep sign
    let offset = gmtOffset?.replace("GMT", "") || "+0";

    // Add :00 if missing minutes
    if (!offset.includes(":")) {
      offset = `${offset}:00`;
    }

    return `(GMT${offset}) ${tz}`;
  } catch (e) {
    console.error("Error formatting timezone:", e);
    return timezone.value;
  }
};

export function convertUTCToTimezone(
  utcDate,
  targetTimezone = null,
  format = false
) {
  try {
    // Get browser timezone if no timezone is specified
    const timezone = targetTimezone || dayjs.tz.guess();

    // Parse UTC date and convert to target timezone
    const convertedDate = dayjs.utc(utcDate).tz(timezone);

    return format
      ? convertedDate.format(format)
      : convertedDate.format("D MMMM YYYY, h:mm A");
  } catch (error) {
    console.error("Error converting date:", error);
    return "Invalid Date";
  }
}

export function convertToTimeZone(dateString, targetTimeZone) {
  // If no timezone provided, use browser's timezone
  const zone = targetTimeZone || dayjs.tz.guess();

  const converted = dayjs.utc(dateString).tz(zone);

  if (!converted.isValid()) {
    return "Invalid date format";
  }

  return converted.format("YYYY-MM-DD HH:mm:ss");
}

export function convertUTCToTimezoneNormalFormat(
  utcDate,
  targetTimezone = null
) {
  try {
    // Get browser timezone if no timezone is specified
    const timezone = targetTimezone || dayjs.tz.guess();

    // Parse UTC date and convert to target timezone
    const convertedDate = dayjs.utc(utcDate).tz(timezone);

    return convertedDate.format("YYYY-MM-DD HH:mm:ss");
  } catch (error) {
    console.error("Error converting date:", error);
    return "Invalid Date";
  }
}
